import React, { ReactNode, RefObject, useRef } from 'react';
import clsx from 'clsx';
import ThreeDotMenu from '../ThreeDotMenu/ThreeDotMenu';
import { useIsMobile } from '../_custom/Hooks/useIsMobile';
import { ACTION_MEASURE_ATTR, PILL_MEASURE_ATTR, useFittedActionCount } from './useFittedActionCount';
import styles from './TabBarActions.module.scss';

export interface TabBarAction {
    id: string;
    /** Must be stateless — dialogs/drawers/copy state live in the bar host so a re-fit never unmounts them. */
    inline: ReactNode;
    menuItem: { name: ReactNode; onClick: () => void; disabled?: boolean };
}

export interface TabBarPill {
    id: string;
    label: string;
    /** The active pill never folds into the menu. */
    active: boolean;
    inline: ReactNode;
    menuItem: TabBarAction['menuItem'];
}

interface TabBarActionsProps {
    actions: TabBarAction[];
    /** Left-aligned sub-tab pills; non-active ones fold into the menu, rightmost first, once nothing else can give. */
    pills?: TabBarPill[];
    pillsRole?: 'tablist';
    /** Sits between the pills and the actions, outside the action group. */
    leading?: ReactNode;
    /** Opt-in: once even the leading's narrowest form and the ⋯ trigger no longer fit, `leading` moves into the menu. */
    leadingMenuItem?: TabBarAction['menuItem'];
    /** Opt-in: `leading` shrinks (its content must ellipsize) once every action is in the menu, before it folds. */
    leadingShrinkable?: boolean;
    /** Forwarded to the ⋯ trigger so a dialog opened from the menu can return focus to it. */
    menuTriggerRef?: RefObject<HTMLButtonElement>;
    className?: string;
}

// Mirror the CSS: .Row/.Pills gap 0.5rem; .Group gap 1.5rem and leading → group 0.5rem + 1rem, at the 16px root.
const PILL_GAP_PX = 8;
const ACTION_GAP_PX = 24;

interface TabBarPillRowProps {
    pills: TabBarPill[];
    /** Indices of the pills folded into the menu. */
    foldedPills: number[];
    allPillsFolded: boolean;
    role?: 'tablist';
}

/** The left-aligned pills that are still inline. */
function TabBarPillRow({ pills, foldedPills, allPillsFolded, role }: Readonly<TabBarPillRowProps>) {
    return (
        <div className={clsx(styles.Pills, allPillsFolded && styles.PillsFloor)} role={role}>
            {pills.map((pill, index) =>
                foldedPills.includes(index) ? null : (
                    <span
                        key={pill.id}
                        className={clsx(styles.Pill, pill.active && styles.PillActive)}
                        {...{ [PILL_MEASURE_ATTR]: pill.id }}
                    >
                        {pill.inline}
                    </span>
                ),
            )}
        </div>
    );
}

interface TabBarActionGroupProps {
    /** Only the actions that stay inline. */
    actions: TabBarAction[];
    overflowItems: TabBarAction['menuItem'][];
    menuTriggerRef?: RefObject<HTMLButtonElement>;
}

/** The right-aligned inline actions plus the ⋯ menu holding everything that folded. */
function TabBarActionGroup({ actions, overflowItems, menuTriggerRef }: Readonly<TabBarActionGroupProps>) {
    return (
        <div className={styles.Group}>
            {actions.map((action) => (
                <span key={action.id} className={styles.Action} {...{ [ACTION_MEASURE_ATTR]: action.id }}>
                    {action.inline}
                </span>
            ))}
            {overflowItems.length > 0 && (
                <ThreeDotMenu glyph="horizontal" triggerRef={menuTriggerRef} tableName="TabBarActionsOverflow" menuItems={overflowItems} />
            )}
        </div>
    );
}

interface TabBarMeasureProbesProps {
    showTriggerProbe: boolean;
    menuItems: TabBarAction['menuItem'][];
    leading?: ReactNode;
    leadingShrinkable: boolean;
    triggerRef: RefObject<HTMLSpanElement>;
    leadingNaturalRef: RefObject<HTMLDivElement>;
    leadingFloorRef: RefObject<HTMLDivElement>;
}

/** Hidden copies of the ⋯ trigger and the leading that the fit measures without depending on what it hides. */
function TabBarMeasureProbes({
    showTriggerProbe,
    menuItems,
    leading,
    leadingShrinkable,
    triggerRef,
    leadingNaturalRef,
    leadingFloorRef,
}: Readonly<TabBarMeasureProbesProps>) {
    return (
        <div className={styles.Probes} aria-hidden="true">
            {showTriggerProbe && (
                <span className={styles.TriggerProbe} ref={triggerRef}>
                    <ThreeDotMenu glyph="horizontal" menuItems={menuItems} />
                </span>
            )}
            {leading && (
                <div className={styles.LeadingNaturalProbe} ref={leadingNaturalRef}>
                    {leading}
                </div>
            )}
            {leading && leadingShrinkable && (
                <div className={styles.LeadingFloorProbe} ref={leadingFloorRef}>
                    {leading}
                </div>
            )}
        </div>
    );
}

/** One tab bar row — pills, an optional leading element and right-aligned actions — that folds into a ⋯ menu when too narrow. */
function TabBarActions({
    actions,
    pills = [],
    pillsRole,
    leading,
    leadingMenuItem,
    leadingShrinkable = false,
    menuTriggerRef,
    className,
}: Readonly<TabBarActionsProps>) {
    const rowRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLSpanElement>(null);
    const leadingNaturalRef = useRef<HTMLDivElement>(null);
    const leadingFloorRef = useRef<HTMLDivElement>(null);
    const foldPills = !useIsMobile();
    const leadingCollapsible = Boolean(leading && leadingMenuItem);
    const activePill = pills.find((pill) => pill.active);
    const fit = useFittedActionCount(
        rowRef,
        {
            pillIds: pills.map((pill) => pill.id),
            pillLabels: pills.map((pill) => pill.label),
            activePillId: activePill?.id,
            actionIds: actions.map((action) => action.id),
            hasLeading: Boolean(leading),
            leadingCollapsible,
            leadingShrinkable,
            foldPills,
            pillGap: PILL_GAP_PX,
            gap: ACTION_GAP_PX,
        },
        { triggerRef, leadingNaturalRef, leadingFloorRef },
    );
    const foldedPills = pills.filter((_, index) => fit.foldedPills.includes(index));
    const spilledActions = actions.slice(0, actions.length - fit.visibleActions);
    const overflowItems = [
        ...foldedPills.map((pill) => pill.menuItem),
        ...(fit.leadingCollapsed && leadingMenuItem ? [leadingMenuItem] : []),
        ...spilledActions.map((action) => action.menuItem),
    ];
    const canFold = actions.length > 0 || leadingCollapsible || (foldPills && pills.some((pill) => !pill.active));
    const allPillsFolded = foldedPills.length > 0 && foldedPills.length === pills.filter((pill) => !pill.active).length;

    return (
        <div className={clsx(styles.Row, className)} ref={rowRef}>
            {pills.length > 0 && (
                <TabBarPillRow pills={pills} foldedPills={fit.foldedPills} allPillsFolded={allPillsFolded} role={pillsRole} />
            )}
            {leading && !fit.leadingCollapsed && (
                <div className={clsx(styles.Leading, leadingShrinkable && styles.LeadingShrinkable)}>{leading}</div>
            )}
            <TabBarActionGroup
                actions={actions.slice(spilledActions.length)}
                overflowItems={overflowItems}
                menuTriggerRef={menuTriggerRef}
            />
            {(canFold || leading) && (
                <TabBarMeasureProbes
                    showTriggerProbe={canFold}
                    menuItems={[
                        ...pills.map((pill) => pill.menuItem),
                        ...(leadingMenuItem ? [leadingMenuItem] : []),
                        ...actions.map((action) => action.menuItem),
                    ]}
                    leading={leading}
                    leadingShrinkable={leadingShrinkable}
                    triggerRef={triggerRef}
                    leadingNaturalRef={leadingNaturalRef}
                    leadingFloorRef={leadingFloorRef}
                />
            )}
        </div>
    );
}

export default TabBarActions;
